/**
 * Repositorio de alertas: misma frontera de migración que dispositivos.
 *
 * Antes no existía. `AlertsContext` llevaba cinco alertas escritas a mano
 * (presión baja, consumo elevado, flujo continuo...) y se las servía a
 * TODAS las cuentas, incluidas las reales. De ahí salía el "5" del menú
 * lateral: no era el número de incidencias de la instalación de nadie, era la
 * longitud de un array del código.
 */

import { mockApi } from '../mock/mockBackend';
import { ApiError, ERROR_CODE } from '../apiError';
import { normalizeAlert, sortAlerts } from '../../domain/alert';
import { isDemoSession } from '../demoSession';
import api from './../api';
import deviceRepository from './deviceRepository';

const usarSimulacion = () => isDemoSession();

function traducirError(err) {
  if (err instanceof ApiError) return err;
  const status = err?.response?.status;
  if (status === 404) return new ApiError(ERROR_CODE.NOT_FOUND, 'Recurso no encontrado');
  return new ApiError(ERROR_CODE.NETWORK, err?.message || 'Error de conexión');
}

export const alertRepository = {
  /**
   * Alertas de todos los dispositivos de la cuenta.
   *
   * `GET /alerts` existe en el backend desplegado y ya filtra por
   * `device_permissions` del token, así que cada usuario recibe sólo las suyas.
   * Una cuenta sin dispositivos recibe una lista vacía —que es exactamente lo
   * que debe verse, no cinco alertas de ejemplo—.
   */
  async list() {
    try {
      if (usarSimulacion()) {
        const alertas = await mockApi.getAccountAlerts();
        return sortAlerts(alertas.map((a) => normalizeAlert(a)));
      }
      /* NO se usa `GET /alerts` aunque la Lambda lo atienda: esa ruta nunca
         se creo en API Gateway. Lo unico desplegado es
         `/devices/{device_id}/alerts`, asi que pedirla devolvia un 403 de ruta
         desconocida que el navegador presenta como fallo de CORS -el preflight
         no llega a responder 200-.

         Se hace en el cliente lo mismo que hace `handleGetAllAlerts` por
         dentro: recorrer los dispositivos del usuario y juntar sus alertas. Con
         uno o dos equipos el coste es identico; si algun dia crece, la
         solucion es crear el recurso `/alerts` en API Gateway y volver a la
         llamada unica, sin tocar a quien consume este repositorio. */
      const dispositivos = await deviceRepository.list();
      const porDispositivo = await Promise.all(
        dispositivos.map(async (d) => {
          try {
            const { data } = await api.get(`devices/${d.device_id}/alerts`, {
              params: { limit: 50, resolved: 'false' },
            });
            return data.alerts ?? data.items ?? (Array.isArray(data) ? data : []);
          } catch (err) {
            // Que falle un dispositivo no debe dejar sin alertas a los demas.
            console.warn(`No se pudieron leer las alertas de ${d.device_id}:`, err.message);
            return [];
          }
        })
      );
      return sortAlerts(porDispositivo.flat().map((a) => normalizeAlert(a)));
    } catch (err) {
      throw traducirError(err);
    }
  },

  /**
   * Marca una alerta como resuelta.
   *
   * `ActiveAlerts` lo hacia con un `setState` y un console.log que decia
   * "(simulado)": la alerta desaparecia de la pantalla y volvia intacta al
   * recargar, porque nunca salio nada hacia el backend. La ruta existe:
   * `POST /devices/{id}/alerts/{alert_id}/resolve`.
   */
  async resolve(deviceId, alertId) {
    try {
      if (usarSimulacion()) return await mockApi.resolveAlert(deviceId, alertId);
      const { data } = await api.post(`devices/${deviceId}/alerts/${alertId}/resolve`);
      return data;
    } catch (err) {
      throw traducirError(err);
    }
  },
};

export default alertRepository;
